import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Q } from '@nozbe/watermelondb';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Boxes,
  CheckCircle2,
  Circle,
  ClipboardList,
  FileText,
  IndianRupee,
  Package,
  PackageX,
  Percent,
  Plus,
  Receipt,
  ShoppingCart,
  Sparkles,
  Users,
  Wallet,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { QuickSaleBar } from '@/components/QuickSaleBar';
import { database } from '@/db';
import { Invoice, LineItem, Payment } from '@/db/models/Billing';
import { Customer } from '@/db/models/Customers';
import { Product } from '@/db/models/Inventory';
import Store from '@/db/models/Store';
import Merchant from '@/db/models/Merchant';
import { useObservable } from '@/hooks/useObservable';
import { useAppStore } from '@/store/useAppStore';
import { formatCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';
import { SalesOverview, type DayPoint } from './dashboard/SalesOverview';
import { PaymentMix, type MethodSlice } from './dashboard/PaymentMix';
import { TopProducts, type TopProduct } from './dashboard/TopProducts';
import { LowStockAlerts, type StockAlert } from './dashboard/LowStockAlerts';
import {
  BILLING_VIEW,
  CUSTOMER_VIEW,
  INVENTORY_VIEW,
  PURCHASING_VIEW,
  REPORTS_VIEW,
  SETTINGS_MANAGE,
  usePermissions,
} from '@/rbac';

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(d: Date): number {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

function greeting(hour: number): string {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

type Tone = 'accent' | 'success' | 'warning' | 'danger';

const TONE_CLASS: Record<Tone, { text: string; soft: string; edge: string }> = {
  accent: { text: 'text-accent-ink', soft: 'bg-accent/10 text-accent-ink', edge: 'border-l-accent' },
  success: { text: 'text-success-ink', soft: 'bg-success/10 text-success-ink', edge: 'border-l-success' },
  warning: { text: 'text-warning-ink', soft: 'bg-warning/10 text-warning-ink', edge: 'border-l-warning' },
  danger: { text: 'text-danger-ink', soft: 'bg-danger/10 text-danger-ink', edge: 'border-l-danger' },
};

// Skeleton bar heights (%) for the empty 7-day chart — reads as "ready for
// data" rather than broken when there are no sales yet.
const SKELETON_BARS = [34, 48, 30, 52, 40, 58, 44];

interface ChecklistStep {
  key: string;
  label: string;
  hint: string;
  done: boolean;
  icon: React.ReactNode;
  cta: string;
  onClick: () => void;
}

const LABELS: Record<string, string> = {
  CASH: 'Cash',
  UPI: 'UPI',
  CARD: 'Card',
  CREDIT: 'Khata',
  BANK_TRANSFER: 'Bank',
  CHEQUE: 'Cheque',
  WALLET: 'Wallet',
};

export function DashboardScreen() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const currentUser = useAppStore((s) => s.currentUser);
  const lastSyncAt = useAppStore((s) => s.lastSyncAt);
  const isSyncing = useAppStore((s) => s.isSyncing);

  const now = useMemo(() => new Date(), []);
  // Pull a window wide enough to cover the current month AND the last 7 days
  // (so the trend chart and "vs yesterday" comparison work at month start).
  const windowStart = useMemo(() => {
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const sevenDaysAgo = startOfDay(new Date(now.getTime() - 6 * DAY_MS));
    return Math.min(startOfMonth, sevenDaysAgo);
  }, [now]);

  const todayStart = useMemo(() => startOfDay(now), [now]);
  const monthStart = useMemo(() => new Date(now.getFullYear(), now.getMonth(), 1).getTime(), [now]);
  const yesterdayStart = todayStart - DAY_MS;

  // ── Live queries ──────────────────────────────────────────────────
  const invoices = useObservable<Invoice[]>(
    () =>
      activeStoreId
        ? database
            .get<Invoice>('invoices')
            .query(
              Q.where('store_id', activeStoreId),
              Q.where('is_deleted', false),
              Q.where('created_at', Q.gte(windowStart)),
              Q.sortBy('created_at', Q.desc),
            )
            .observe()
        : null,
    [activeStoreId, windowStart],
    [],
  );

  const products = useObservable<Product[]>(
    () =>
      activeStoreId
        ? database
            .get<Product>('products')
            .query(
              Q.where('store_id', activeStoreId),
              Q.where('is_deleted', false),
              Q.where('is_active', true),
            )
            .observe()
        : null,
    [activeStoreId],
    [],
  );

  const customers = useObservable<Customer[]>(
    () =>
      activeStoreId
        ? database
            .get<Customer>('customers')
            .query(Q.where('store_id', activeStoreId), Q.where('is_deleted', false))
            .observe()
        : null,
    [activeStoreId],
    [],
  );

  const stores = useObservable<Store[]>(
    () =>
      activeStoreId
        ? database.get<Store>('stores').query(Q.where('id', activeStoreId)).observe()
        : null,
    [activeStoreId],
    [],
  );
  const storeName = stores[0]?.name ?? '';

  // Onboarding signals for the first-run checklist. Merchant carries the
  // primary GSTIN; the store may override it. Sales count is all-time (not the
  // windowed `invoices` above) so an earlier sale still marks the step done.
  const merchantId = currentUser?.merchantId;
  const merchants = useObservable<Merchant[]>(
    () =>
      merchantId
        ? database.get<Merchant>('merchants').query(Q.where('id', merchantId)).observe()
        : null,
    [merchantId],
    [],
  );
  const salesCount = useObservable<number>(
    () =>
      activeStoreId
        ? database
            .get<Invoice>('invoices')
            .query(
              Q.where('store_id', activeStoreId),
              Q.where('is_deleted', false),
              Q.where('type', 'SALE'),
            )
            .observeCount()
        : null,
    [activeStoreId],
    0,
  );

  // ── Insight widgets ───────────────────────────────────────────────
  // Line items and payments are pulled for the SAME window as `invoices`, then
  // joined in memory against the invoice ids already on screen. Two indexed
  // reads beat a per-invoice fetch, and the window keeps both bounded.
  const lineItems = useObservable<LineItem[]>(
    () =>
      activeStoreId
        ? database
            .get<LineItem>('line_items')
            .query(Q.where('is_deleted', false), Q.where('created_at', Q.gte(windowStart)))
            .observe()
        : null,
    [activeStoreId, windowStart],
    [],
  );

  const payments = useObservable<Payment[]>(
    () =>
      activeStoreId
        ? database
            .get<Payment>('payments')
            .query(Q.where('is_deleted', false), Q.where('received_at', Q.gte(windowStart)))
            .observe()
        : null,
    [activeStoreId, windowStart],
    [],
  );

  // ── Insight derivations ───────────────────────────────────────────
  const DAYS = 14;

  /** Net sales per local day. Credit notes subtract, so a refund shows. */
  const salesByDay = useMemo<DayPoint[]>(() => {
    const days: DayPoint[] = [];
    const todayStart = startOfDay(now);
    for (let i = DAYS - 1; i >= 0; i--) days.push({ day: todayStart - i * DAY_MS, total: 0 });
    const index = new Map(days.map((d, i) => [d.day, i]));
    for (const inv of invoices) {
      const key = startOfDay(new Date(inv.createdAt));
      const at = index.get(key);
      if (at == null) continue;
      days[at].total += inv.type === 'CREDIT_NOTE' ? -inv.totalAmount : inv.totalAmount;
    }
    // A net-negative day would invert the area; clamp for the plot only.
    return days.map((d) => ({ ...d, total: Math.max(0, d.total) }));
  }, [invoices, now]);

  /** Today's money, grouped by how it arrived. */
  const paymentSlices = useMemo<MethodSlice[]>(() => {
    const todayStart = startOfDay(now);
    const byMethod = new Map<string, number>();
    for (const pay of payments) {
      if (new Date(pay.receivedAt).getTime() < todayStart) continue;
      byMethod.set(pay.method, (byMethod.get(pay.method) ?? 0) + pay.amount);
    }
    const KNOWN = ['CASH', 'UPI', 'CARD'];
    const slices: MethodSlice[] = [];
    for (const m of KNOWN) {
      const amt = byMethod.get(m) ?? 0;
      if (amt > 0) slices.push({ method: m, label: LABELS[m] ?? m, amount: amt });
    }
    // Everything else folds into one neutral slice rather than growing the
    // palette — a fourth hue would be indistinguishable under CVD.
    let other = 0;
    for (const [m, amt] of byMethod) if (!KNOWN.includes(m)) other += amt;
    if (other > 0) slices.push({ method: 'OTHER', label: 'Other', amount: other });
    return slices;
  }, [payments, now]);

  /** Units sold per product over the last 7 days, top 5. */
  const topProducts = useMemo<TopProduct[]>(() => {
    const since = startOfDay(now) - 6 * DAY_MS;
    const saleIds = new Set(
      invoices
        .filter((i) => i.type === 'SALE' && new Date(i.createdAt).getTime() >= since)
        .map((i) => i.id),
    );
    const byProduct = new Map<string, number>();
    for (const li of lineItems) {
      if (!saleIds.has(li.invoiceId)) continue;
      byProduct.set(li.productId, (byProduct.get(li.productId) ?? 0) + li.quantity);
    }
    const nameOf = new Map(products.map((p) => [p.id, p]));
    return [...byProduct.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([productId, qty]) => ({
        productId,
        name: nameOf.get(productId)?.name ?? 'Unknown product',
        qty,
        unit: nameOf.get(productId)?.unit ?? null,
      }));
  }, [lineItems, invoices, products, now]);

  /** Out of stock first — it is already costing sales, not about to. */
  const stockAlerts = useMemo<StockAlert[]>(() => {
    return products
      .filter((p) => p.trackInventory && !p.isService && p.currentStock <= p.lowStockAlert)
      .map((p) => ({
        productId: p.id,
        name: p.name,
        stock: p.currentStock,
        unit: p.unit,
        outOfStock: p.currentStock <= 0,
      }))
      .sort((a, b) => Number(b.outOfStock) - Number(a.outOfStock) || a.stock - b.stock)
      .slice(0, 5);
  }, [products]);

  // ── Derived metrics ───────────────────────────────────────────────
  const customerNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of customers) map.set(c.id, c.name || 'Customer');
    return map;
  }, [customers]);

  const isSale = (inv: Invoice) => inv.type === 'SALE';

  const metrics = useMemo(() => {
    let todaySales = 0;
    let todayCount = 0;
    let todayCollected = 0;
    let monthSales = 0;
    let monthCount = 0;
    let yesterdaySales = 0;

    // 7-day buckets, index 0 = 6 days ago … index 6 = today
    const trend = new Array(7).fill(0) as number[];
    const trendStart = startOfDay(new Date(now.getTime() - 6 * DAY_MS));

    for (const inv of invoices) {
      const t = inv.createdAt instanceof Date ? inv.createdAt.getTime() : Number(inv.createdAt);
      const amount = inv.totalAmount || 0;
      const sale = isSale(inv);

      if (sale && t >= trendStart) {
        const idx = Math.floor((startOfDay(new Date(t)) - trendStart) / DAY_MS);
        if (idx >= 0 && idx < 7) trend[idx] += amount;
      }
      if (t >= monthStart && sale) {
        monthSales += amount;
        monthCount += 1;
      }
      if (t >= todayStart) {
        if (sale) {
          todaySales += amount;
          todayCount += 1;
        }
        todayCollected += inv.paidAmount || 0;
      } else if (t >= yesterdayStart && sale) {
        yesterdaySales += amount;
      }
    }

    const avgBill = todayCount > 0 ? todaySales / todayCount : 0;
    const deltaPct =
      yesterdaySales > 0 ? ((todaySales - yesterdaySales) / yesterdaySales) * 100 : null;

    return { todaySales, todayCount, todayCollected, monthSales, monthCount, avgBill, deltaPct, trend };
  }, [invoices, now, todayStart, monthStart, yesterdayStart]);

  const outstanding = useMemo(
    () => customers.reduce((sum, c) => sum + (c.balance > 0 ? c.balance : 0), 0),
    [customers],
  );

  const lowStock = useMemo(
    () =>
      products
        .filter((p) => p.trackInventory && !p.isService)
        .filter((p) => p.lowStockAlert > 0 && p.currentStock <= p.lowStockAlert)
        .sort((a, b) => a.currentStock - b.currentStock),
    [products],
  );
  const outOfStock = useMemo(
    () => products.filter((p) => p.trackInventory && !p.isService && p.currentStock <= 0).length,
    [products],
  );

  const recentInvoices = useMemo(() => invoices.slice(0, 6), [invoices]);

  const trendMax = Math.max(...metrics.trend, 1);
  const hasTrend = metrics.trend.some((v) => v > 0);
  const syncText = lastSyncAt
    ? new Date(lastSyncAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
    : '—';

  // ── First-run checklist ───────────────────────────────────────────
  // Guides a new store to its first real value. Each step is gated by the
  // permission needed to complete it, so a cashier only sees steps they can
  // act on. The whole card hides once every visible step is done.
  const gstConfigured = Boolean(merchants[0]?.gstin || stores[0]?.gstinOverride);
  const onboardingSteps: ChecklistStep[] = [
    can(INVENTORY_VIEW) && {
      key: 'product',
      label: 'Add your first product',
      hint: 'Build your catalog so you can start billing.',
      done: products.length > 0,
      icon: <Package size={15} />,
      cta: 'Add product',
      onClick: () => navigate('/inventory'),
    },
    can(SETTINGS_MANAGE) && {
      key: 'gst',
      label: 'Add your GST & store details',
      hint: 'Print GST-compliant invoices with your business info.',
      done: gstConfigured,
      icon: <Percent size={15} />,
      cta: 'Open settings',
      onClick: () => navigate('/settings'),
    },
    can(BILLING_VIEW) && {
      key: 'sale',
      label: 'Make your first sale',
      hint: 'Ring up a test bill to see it flow end-to-end.',
      done: salesCount > 0,
      icon: <ShoppingCart size={15} />,
      cta: 'New sale',
      onClick: () => navigate('/billing'),
    },
  ].filter(Boolean) as ChecklistStep[];
  const onboardingDoneCount = onboardingSteps.filter((s) => s.done).length;
  const showChecklist = onboardingSteps.length > 0 && onboardingDoneCount < onboardingSteps.length;

  // ── Render ────────────────────────────────────────────────────────
  return (
    // -m-8 p-8 cancels the content sheet's own padding and re-adds it inside,
    // so the tinted field reaches the sheet's edges instead of leaving a plain
    // white frame around it. The sheet clips to its radius, so the corners
    // stay rounded.
    <div className="glass-field -m-8 space-y-6 p-8 pb-16">
      {/* Header */}
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-display text-fg-primary">
            {greeting(now.getHours())}
            {currentUser?.name ? `, ${currentUser.name.split(' ')[0]}` : ''}
          </h1>
          <p className="mt-1 text-body text-fg-secondary">
            {now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            {storeName && <span className="text-fg-tertiary"> · {storeName}</span>}
          </p>
        </div>
        <div
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-label font-medium',
            isSyncing ? 'bg-warning/10 text-warning-ink' : 'bg-success/10 text-success-ink',
          )}
        >
          <span
            className={cn(
              'inline-flex h-2 w-2 rounded-full',
              isSyncing ? 'animate-pulse bg-warning' : 'bg-success',
            )}
            aria-hidden
          />
          {isSyncing ? 'Syncing…' : `Synced ${syncText}`}
        </div>
      </header>

      {/* Quick-sale scan bar — scan/search a product to start billing instantly */}
      {can(BILLING_VIEW) && <QuickSaleBar />}

      {/* KPI grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={<IndianRupee size={18} />}
          tone="accent"
          label="Today’s sales"
          value={formatCurrency(metrics.todaySales)}
          onClick={can(REPORTS_VIEW) ? () => navigate('/reports') : undefined}
          footer={
            <div className="space-y-2">
              {metrics.deltaPct == null ? (
                <span className="text-fg-tertiary">No sales yesterday to compare</span>
              ) : (
                <TrendBadge pct={metrics.deltaPct} />
              )}
              <Sparkline data={metrics.trend} className="text-accent-ink/50" />
            </div>
          }
        />
        <StatCard
          icon={<Receipt size={18} />}
          tone="success"
          label="Invoices today"
          value={String(metrics.todayCount)}
          onClick={can(BILLING_VIEW) ? () => navigate('/invoices') : undefined}
          footer={<span className="text-fg-tertiary">Avg bill {formatCurrency(metrics.avgBill)}</span>}
        />
        <StatCard
          icon={<Wallet size={18} />}
          tone="success"
          label="Collected today"
          value={formatCurrency(metrics.todayCollected)}
          footer={<span className="text-fg-tertiary">Cash + digital received</span>}
        />
        <StatCard
          icon={<Users size={18} />}
          tone={outstanding > 0 ? 'danger' : 'success'}
          label="Outstanding (Khata)"
          value={formatCurrency(outstanding)}
          onClick={can(CUSTOMER_VIEW) ? () => navigate('/customers') : undefined}
          footer={<span className="text-fg-tertiary">{customers.length} customers</span>}
        />
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap gap-3">
        {can(BILLING_VIEW) && (
          <QuickAction icon={<ShoppingCart size={16} />} label="New sale" primary onClick={() => navigate('/billing')} />
        )}
        {can(INVENTORY_VIEW) && (
          <QuickAction icon={<Plus size={16} />} label="Inventory" onClick={() => navigate('/inventory')} />
        )}
        {can(PURCHASING_VIEW) && (
          <QuickAction icon={<ClipboardList size={16} />} label="Purchase" onClick={() => navigate('/purchases/new')} />
        )}
        {can(CUSTOMER_VIEW) && (
          <QuickAction icon={<Users size={16} />} label="Customers" onClick={() => navigate('/customers')} />
        )}
        {can(REPORTS_VIEW) && (
          <QuickAction icon={<FileText size={16} />} label="Reports" onClick={() => navigate('/reports')} />
        )}
      </div>

      {/* First-run checklist — shown only until the store is set up */}
      {showChecklist && (
        <FirstRunChecklist steps={onboardingSteps} doneCount={onboardingDoneCount} />
      )}

      {/* Insight row.
       *
       * Sales leads at two thirds because it is the question the owner opens
       * the app to answer; the payment split is context beside it. Both sit
       * above the operational lists, and the grid collapses to one column
       * rather than squeezing a chart into a third of a laptop screen. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SalesOverview data={salesByDay} />
        </div>
        <PaymentMix slices={paymentSlices} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <TopProducts items={topProducts} />
        <LowStockAlerts items={stockAlerts} />
      </div>

      {/* Main grid: trend + recent | alerts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Left column (2/3) */}
        <div className="space-y-4 lg:col-span-2">
          {/* 7-day trend */}
          <Card>
            <div className="flex items-center justify-between">
              <h2 className="text-heading text-fg-primary">Last 7 days</h2>
              <span className="text-label uppercase text-fg-tertiary">
                This month {formatCurrency(metrics.monthSales)} · {metrics.monthCount} bills
              </span>
            </div>
            <div className="mt-5 flex h-36 items-end gap-2">
              {metrics.trend.map((val, i) => {
                const date = new Date(now.getTime() - (6 - i) * DAY_MS);
                const isToday = i === 6;
                return (
                  <div key={i} className="flex flex-1 flex-col items-center gap-2">
                    <div className="flex w-full flex-1 items-end">
                      <div
                        className={cn(
                          'w-full rounded-t-md transition-all duration-240',
                          !hasTrend ? 'bg-bg-subtle' : isToday ? 'bg-accent' : 'bg-accent/35',
                        )}
                        style={{
                          height: hasTrend
                            ? `${Math.max(4, (val / trendMax) * 100)}%`
                            : `${SKELETON_BARS[i]}%`,
                        }}
                        title={hasTrend ? formatCurrency(val) : undefined}
                      />
                    </div>
                    <span className={cn('text-label', isToday ? 'text-fg-primary' : 'text-fg-tertiary')}>
                      {date.toLocaleDateString('en-IN', { weekday: 'short' })}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Recent invoices */}
          <Card className="p-0">
            <div className="flex items-center justify-between px-5 py-4">
              <h2 className="text-heading text-fg-primary">Recent invoices</h2>
              {can(BILLING_VIEW) && (
                <button
                  type="button"
                  className="text-label uppercase text-accent-ink hover:underline"
                  onClick={() => navigate('/invoices')}
                >
                  View all
                </button>
              )}
            </div>
            <div className="divide-y divide-stroke-hairline/10">
              {recentInvoices.length === 0 ? (
                <div className="flex flex-col items-center gap-3 px-5 py-10 text-center">
                  <span className="text-fg-tertiary"><Receipt size={20} /></span>
                  <p className="text-body text-fg-tertiary">No invoices yet today.</p>
                  {can(BILLING_VIEW) && (
                    <button
                      type="button"
                      onClick={() => navigate('/billing')}
                      className="btn-brand inline-flex items-center gap-2 rounded-md border border-white/10 px-4 py-2.5 text-body font-medium transition-colors duration-120"
                    >
                      <Plus size={16} /> Create first sale
                    </button>
                  )}
                </div>
              ) : (
                recentInvoices.map((inv) => (
                  <button
                    key={inv.id}
                    type="button"
                    onClick={() => can(BILLING_VIEW) && navigate(`/invoices/${inv.id}`)}
                    className="flex w-full items-center justify-between px-5 py-3 text-left transition-colors duration-120 hover:bg-bg-subtle"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-body font-medium text-fg-primary">
                        {inv.invoiceNumber}
                        {inv.type !== 'SALE' && (
                          <span className="ml-2 text-label uppercase text-warning-ink">{inv.type.replace('_', ' ')}</span>
                        )}
                      </p>
                      <p className="truncate text-label text-fg-tertiary">
                        {inv.customerId ? customerNames.get(inv.customerId) || 'Customer' : 'Walk-in'} ·{' '}
                        {(inv.createdAt instanceof Date ? inv.createdAt : new Date(inv.createdAt)).toLocaleTimeString(
                          'en-IN',
                          { hour: '2-digit', minute: '2-digit' },
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 pl-3">
                      <span className="text-body font-semibold tabular-nums text-fg-primary">
                        {formatCurrency(inv.totalAmount)}
                      </span>
                      <PaymentBadge status={inv.paymentStatus} />
                    </div>
                  </button>
                ))
              )}
            </div>
          </Card>
        </div>

        {/* Right column (1/3) — alerts */}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <MiniStat icon={<Boxes size={16} />} label="Products" value={String(products.length)} />
            <MiniStat
              icon={<PackageX size={16} />}
              label="Out of stock"
              value={String(outOfStock)}
              tone={outOfStock > 0 ? 'danger' : undefined}
            />
          </div>

          {lowStock.length === 0 ? (
            <Card className="flex flex-col items-center gap-2 border-success/20 bg-success/5 py-8 text-center">
              <CheckCircle2 size={28} className="text-success-ink" />
              <p className="text-heading text-success-ink">All stock healthy</p>
              <p className="text-label text-fg-tertiary">No low-stock alerts</p>
            </Card>
          ) : (
          <Card className="p-0">
            <div className="flex items-center justify-between px-5 py-4">
              <h2 className="flex items-center gap-2 text-heading text-fg-primary">
                <AlertTriangle size={16} className="text-warning-ink" />
                Low stock
              </h2>
              {can(INVENTORY_VIEW) && (
                <button
                  type="button"
                  className="text-label uppercase text-accent-ink hover:underline"
                  onClick={() => navigate('/inventory')}
                >
                  Manage
                </button>
              )}
            </div>
            <div className="divide-y divide-stroke-hairline/10">
              {lowStock.slice(0, 6).map((p) => (
                  <div key={p.id} className="flex items-center justify-between px-5 py-3">
                    <p className="min-w-0 truncate text-body text-fg-primary">{p.name}</p>
                    <span
                      className={cn(
                        'ml-3 shrink-0 rounded-sm px-2 py-0.5 text-label tabular-nums',
                        p.currentStock <= 0 ? 'bg-danger/10 text-danger-ink' : 'bg-warning/10 text-warning-ink',
                      )}
                    >
                      {p.currentStock} {p.unit || ''} left
                    </span>
                  </div>
                ))}
              {lowStock.length > 6 && (
                <p className="px-5 py-2 text-label text-fg-tertiary">+{lowStock.length - 6} more below threshold</p>
              )}
            </div>
          </Card>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
//  Presentational sub-components
// ════════════════════════════════════════════════════════════════════

function FirstRunChecklist({ steps, doneCount }: { steps: ChecklistStep[]; doneCount: number }) {
  const total = steps.length;
  return (
    <Card className="border-l-[3px] border-l-accent">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-heading text-fg-primary">
          <Sparkles size={18} className="text-accent-ink" />
          Get started
        </h2>
        <span className="text-label uppercase text-fg-tertiary">
          {doneCount} of {total} done
        </span>
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-bg-subtle">
        <div
          className="h-full rounded-full bg-accent transition-all duration-240"
          style={{ width: `${total > 0 ? (doneCount / total) * 100 : 0}%` }}
        />
      </div>
      <div className="mt-4 space-y-2">
        {steps.map((step) => (
          <ChecklistRow key={step.key} step={step} />
        ))}
      </div>
    </Card>
  );
}

function ChecklistRow({ step }: { step: ChecklistStep }) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-md border border-white/60 bg-white/45 px-4 py-3 backdrop-blur-sm',
        step.done && 'opacity-60',
      )}
    >
      <span className={step.done ? 'text-success-ink' : 'text-fg-tertiary'} aria-hidden>
        {step.done ? <CheckCircle2 size={20} /> : <Circle size={20} />}
      </span>
      <div className="min-w-0 flex-1">
        <p className={cn('text-body font-medium text-fg-primary', step.done && 'line-through')}>
          {step.label}
        </p>
        <p className="truncate text-label text-fg-tertiary">{step.hint}</p>
      </div>
      {!step.done && (
        <button
          type="button"
          onClick={step.onClick}
          className="btn-brand inline-flex shrink-0 items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-label font-medium transition-colors duration-120"
        >
          {step.icon}
          {step.cta}
        </button>
      )}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  footer,
  tone,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  footer?: React.ReactNode;
  tone: Tone;
  onClick?: () => void;
}) {
  const t = TONE_CLASS[tone];
  return (
    <Card
      onClick={onClick}
      className={cn(
        'flex flex-col gap-3 border-l-[3px]',
        t.edge,
        // shadow-glass-lg, not shadow-e2: e2 is a flat drop that would replace
        // the card's lit top edge and break the glass on hover.
        onClick && 'cursor-pointer transition-shadow duration-180 hover:shadow-glass-lg',
      )}
    >
      <div className="flex items-center justify-between">
        <p className="text-label uppercase text-fg-secondary">{label}</p>
        <span className={cn('flex h-8 w-8 items-center justify-center rounded-md', t.soft)}>{icon}</span>
      </div>
      <p className={cn('text-display tabular-nums', t.text)}>{value}</p>
      {footer && <div className="text-label">{footer}</div>}
    </Card>
  );
}

function TrendBadge({ pct }: { pct: number }) {
  const up = pct >= 0;
  return (
    <span className={cn('inline-flex items-center gap-1', up ? 'text-success-ink' : 'text-danger-ink')}>
      {up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
      {Math.abs(pct).toFixed(0)}% vs yesterday
    </span>
  );
}

function QuickAction({
  icon,
  label,
  onClick,
  primary,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 rounded-md border px-4 py-2.5 text-body font-medium transition-colors duration-120',
        primary
          ? 'btn-brand border-white/10'
          // Frosted pill rather than a transparent one: on the tinted field a
          // bare button has no edge at all until you hover it.
          : 'btn-brand-soft border-ink-500/25',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function MiniStat({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone?: Tone;
}) {
  return (
    <Card className="flex flex-col gap-1 p-4">
      <span className="flex items-center gap-1.5 text-label uppercase text-fg-secondary">
        {icon}
        {label}
      </span>
      <span className={cn('text-heading tabular-nums', tone ? TONE_CLASS[tone].text : 'text-fg-primary')}>
        {value}
      </span>
    </Card>
  );
}

function PaymentBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    PAID: 'bg-success/10 text-success-ink',
    PARTIAL: 'bg-warning/10 text-warning-ink',
    UNPAID: 'bg-danger/10 text-danger-ink',
    PENDING: 'bg-danger/10 text-danger-ink',
  };
  return (
    <span className={cn('rounded-sm px-2 py-0.5 text-label uppercase', map[status] || 'bg-bg-subtle text-fg-tertiary')}>
      {status}
    </span>
  );
}

function Sparkline({ data, className }: { data: number[]; className?: string }) {
  const max = Math.max(...data, 1);
  const w = 100;
  const h = 24;
  const step = data.length > 1 ? w / (data.length - 1) : w;
  const points = data
    .map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * (h - 2) - 1).toFixed(1)}`)
    .join(' ');
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className={cn('h-6 w-full', className)}
      aria-hidden
    >
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
